import { reportCurrency } from '@evi/shared';
import { EarningsAmounts, PayoutAmount } from '@evi/ui';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, DollarSign, ExternalLink, Leaf, Loader2 } from 'lucide-react';
import { RevenueChart } from '@evi/ui';
import {
  fetchConnectStatus,
  fetchDashboardLink,
  fetchMyCarbonSummary,
  fetchMyCharger,
  fetchMyChargerEarnings,
  fetchMyChargerRevenueSeries,
  formatCents,
  startPayoutsOrChoose,
  type MyChargerEarnings,
} from '@/lib/api';
import { currencySymbol } from '@/lib/money';
import { payoutChooserPath } from '@/lib/payoutCountry';
import { earningsCopy } from '@/lib/guestPricing';
import { toast } from '@/store/toast';
import styles from './MyChargerEarningsPage.module.css';

type EarningsChartPeriod = '7d' | '30d';

interface Props {
  chargerId: string;
  onNavigate: (path: string) => void;
}

/**
 * Earnings summary for a resident-owned charger. Aggregates across
 * the Payout collection's terminal-success rows (status = paid OR
 * transferred) so the resident sees cleared dollars only — pending
 * or failed payouts don't inflate the headline.
 *
 * The Stripe Express dashboard CTA branches on Connect-readiness:
 *   - charges_enabled === true  → "Open Stripe dashboard." Click
 *     mints a one-time login URL via POST /me/connect/dashboard-link
 *     and opens it in a new tab. The new tab opens SYNCHRONOUSLY in
 *     the click handler (about:blank, redirected after the fetch
 *     resolves) so iOS Safari's popup heuristic honors it — same
 *     trick used by the location-review CTA (5ef3794).
 *   - charges_enabled === false → "Set up payouts." Click kicks off
 *     POST /me/connect/start (full-page redirect to Stripe-hosted
 *     onboarding). Same path the MyChargerDetailPage gate block
 *     uses, kept consistent so the resident doesn't see different
 *     onboarding routes from two surfaces. With no account yet (or a
 *     status it could not read) it goes to the payout chooser instead
 *     (item 2), whose back button returns here: only there is the
 *     bank account country asked for, and the server creates no
 *     account without one. The carbon card's CTA and the dashboard
 *     412 fallback follow the same rule (startPayoutsOrChoose).
 */
export function MyChargerEarningsPage({ chargerId, onNavigate }: Props) {
  const { t, i18n } = useTranslation('myChargers');
  const lang = i18n.language;
  // The payout chooser, with a way back to this page.
  const chooserPath = payoutChooserPath(`/my-chargers/${chargerId}/earnings`);
  const { data, isLoading, error } = useQuery({
    queryKey: ['my-charger', chargerId, 'earnings'],
    queryFn: () => fetchMyChargerEarnings(chargerId),
    staleTime: 30_000,
  });

  // Period-toggled revenue trend. Local state (not URL-synced) —
  // the page has no other period UI to consult, and earnings-page
  // refreshes are infrequent enough that the chip's "remember on
  // back" benefit doesn't pay for the URL plumbing. Default 7d
  // matches the driver-scoped HistoryPage default.
  const [chartPeriod, setChartPeriod] = useState<EarningsChartPeriod>('7d');
  const revenueSeriesQ = useQuery({
    queryKey: ['my-charger', chargerId, 'revenue-series', chartPeriod],
    queryFn: () => fetchMyChargerRevenueSeries(chargerId, chartPeriod),
    staleTime: 30_000,
  });

  // Carbon-credit summary — HOST-level (all the owner's chargers),
  // deliberately separate from the per-charger guest-revenue numbers
  // above: guest revenue is realized cash; carbon is a three-state
  // program balance and must never blur into it.
  const carbonQ = useQuery({
    queryKey: ['me', 'carbon-summary'],
    queryFn: fetchMyCarbonSummary,
    staleTime: 60_000,
  });
  const [carbonSetupBusy, setCarbonSetupBusy] = useState(false);

  // Connect status drives the Stripe-row CTA branching. Shared
  // queryKey with ConnectCompletePage so a single fetch covers both
  // surfaces when the user moves between them. 60s staleTime —
  // onboarding state changes are user-driven (the resident either
  // taps through Stripe or doesn't), so we don't need aggressive
  // refetch here. ConnectCompletePage refreshes on focus when the
  // resident returns from Stripe-hosted onboarding.
  const connectQ = useQuery({
    queryKey: ['connect-status'],
    queryFn: fetchConnectStatus,
    staleTime: 60_000,
  });

  // Item 6: the charger itself, for which earning copy applies. Same key
  // as the charger page, so coming from there it is already cached. A
  // building resident's charger has no paid guests yet, so no "open it
  // with a markup" advice and no payout setup to push. A resident who
  // already set payouts up keeps the dashboard link for their history.
  const chargerQ = useQuery({
    queryKey: ['my-charger', chargerId],
    queryFn: () => fetchMyCharger(chargerId),
    staleTime: 30_000,
  });
  const copyKind = earningsCopy(chargerQ.data);
  const buildingNoPayouts = copyKind === 'building'
    && !connectQ.isLoading
    && connectQ.data?.charges_enabled !== true;

  // Per-click in-flight state for the dashboard-link mint. Distinct
  // from connectQ.isLoading (which only fires on first mount) so
  // tapping the button shows a spinner specifically for the
  // dashboard-link fetch, not the unrelated connect-status fetch.
  const [openingDashboard, setOpeningDashboard] = useState(false);
  // Onboarding flow is a full-page redirect — no need for a separate
  // loading state on the page since the user's about to leave. We
  // still gate the button so a rapid double-tap doesn't fire two
  // /me/connect/start requests.
  const [startingOnboarding, setStartingOnboarding] = useState(false);

  // "Empty" = literally zero across every bucket. We still render
  // the cards (with $0.00) but swap the page subtitle to nudge the
  // resident toward expectations.
  const isEmpty =
    data &&
    data.all_time_cents === 0 &&
    data.this_month_cents === 0 &&
    data.last_30_days_cents === 0 &&
    !data.last_payout &&
    !data.by_currency;

  // One line per currency, never a blended total (item 3): a home with
  // USD history beside CAD sessions shows each. An Api that predates the
  // field sends one USD set.
  const lines = data ? earningsLines(data) : [];
  const currentCurrency = reportCurrency(data && 'current_currency' in data ? data.current_currency
    : chargerQ.data?.home_monetization?.guest_rate_currency ?? data?.currency);
  const moneyLines = (pick: (l: EarningsLine) => number): MoneyItem[] =>
    lines.map((l) => ({ cents: pick(l), currency: l.currency }));
  // The last payout is ONE payout, in its own currency: the latest.
  const lastPayout = lines
    .map((l) => l.last_payout ? { ...l.last_payout, currency: l.last_payout.currency ?? l.currency } : null)
    .filter((p): p is NonNullable<typeof p> => p !== null)
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0] ?? null;
  // The chart is the primary currency's series; the footer names each.
  const series = revenueSeriesQ.data;
  const seriesCurrency = currentCurrency ?? series?.currency ?? data?.currency ?? 'USD';
  const seriesLines = series
    ? (series.by_currency ?? [{
        currency: series.currency ?? data?.currency ?? 'USD', buckets: series.buckets,
        totalEarned: series.totalEarned, sessionCount: series.sessionCount,
      }])
    : [];
  const currentSeries = seriesLines.find(x => x.currency === seriesCurrency);
  const chartBuckets = currentSeries?.buckets ?? series?.buckets.map(() => 0) ?? [];
  // The axis is drawn at 10px in a 40px gutter, too narrow for "CA$100".
  // A bare "$" keeps the chart's own "$100"; any other symbol prints the
  // number only, and the chart title names the currency.
  const axisSymbol = currencySymbol(seriesCurrency, lang);
  const formatAxis = (n: number): string =>
    axisSymbol === '$' ? `$${Math.round(n)}` : String(Math.round(n));


  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <button
          type="button"
          className={styles.backFab}
          aria-label={t('earnings.back')}
          onClick={() => onNavigate(`/my-chargers/${chargerId}`)}
        >
          <ArrowLeft size={16} />
        </button>
        <div className={styles.titleBlock}>
          <h1 className={styles.title}>{t('earnings.title')}</h1>
          <p className={styles.subtitle}>
            {isLoading
              ? t('earnings.loading')
              : isEmpty
                ? t('earnings.subtitle.empty')
                : t('earnings.subtitle.ready')}
          </p>
        </div>
      </header>

      {isLoading && (
        <>
          <div className={styles.heroSkeleton} />
          <div className={styles.gridSkeleton} />
        </>
      )}

      {!isLoading && error && (
        <div className={styles.errorBox}>
          {error instanceof Error ? error.message : t('earnings.errorLoad')}
        </div>
      )}

      {!isLoading && !error && data && (
        <>
          {/* Headline */}
          <div className={styles.hero}>
            <div className={styles.heroIcon}>
              <DollarSign size={20} />
            </div>
            <div className={styles.heroBody}>
              <p className={styles.heroLabel}>{t('earnings.heroAllTime')}</p>
              <p className={styles.heroValue}>
                <EarningsAmounts current={[currentCurrency]} items={moneyLines((l) => l.all_time_cents)} locale={lang} />
              </p>
            </div>
          </div>

          {/* Revenue trend — period-toggled chart sitting between the
              all-time hero and the month/30d/payout breakdown. Reads
              from the same PayoutModel source as the cards (status
              ∈ {paid, transferred}) so the 30d total in the footer
              pill reconciles with the "Last 30 days" card below.
              Rendered only after the series query resolves; we lean
              on the page's own header skeleton for the loading
              moment and avoid stacking a second placeholder. */}
          {series && (
            <div className={styles.revenueChartWrap}>
              <RevenueChart
                data={chartBuckets}
                formatY={formatAxis}
                // The chart's own title, in the series' currency: the
                // default reads "REVENUE: USD" at every home.
                titleLabel={`REVENUE: ${seriesCurrency}`}
                currentRate={0}
                period={chartPeriod}
                onPeriodChange={(next) => setChartPeriod(next as EarningsChartPeriod)}
                showRate={false}
                allowedPeriods={['7d', '30d']}
                footer={<EarningsAmounts
                  items={seriesLines.map(x => ({ currency: x.currency, cents: Math.round(x.totalEarned * 100) }))}
                  current={[currentCurrency]} locale={lang} />}
              />
            </div>
          )}

          {/* Three sub-cards */}
          <div className={styles.grid}>
            <SummaryCard
              label={t('earnings.cards.thisMonth')}
              value={<EarningsAmounts current={[currentCurrency]} items={moneyLines((l) => l.this_month_cents)} locale={lang} />}
            />
            <SummaryCard
              label={t('earnings.cards.last30')}
              value={<EarningsAmounts current={[currentCurrency]} items={moneyLines((l) => l.last_30_days_cents)} locale={lang} />}
            />
            <SummaryCard
              label={t('earnings.cards.lastPayout')}
              value={lastPayout
                ? <PayoutAmount cents={lastPayout.amount_cents} currency={lastPayout.currency} cash={lastPayout.cash_by_currency} locale={lang} />
                : '—'}
              hint={lastPayout
                ? formatDate(new Date(lastPayout.date))
                : t('earnings.cards.noPayouts')}
            />
          </div>

          {/* Carbon credits — HOST-level program balance, visually
              distinct from the guest-revenue cards above (green
              surface, Leaf mark). THREE states, never blurred:
              accruing = ESTIMATE with the annual payout window (a
              climbing number with no date is the program's #1 future
              support ticket); realized = confirmed money; realized-
              but-unpaid = earned, waiting on Stripe onboarding, with
              the setup CTA. */}
          {carbonQ.data && carbonQ.data.currencies.length > 0 && (
            <div className={styles.carbonCard}>
              <div className={styles.carbonHeader}>
                <Leaf size={15} aria-hidden />
                <span>{t('earnings.carbon.title')}</span>
              </div>
              {carbonQ.data.currencies.map((b) => (
                <div key={b.currency} className={styles.carbonRows}>
                  {b.accruing_estimate_cents > 0 && (
                    <div className={styles.carbonRow}>
                      <div className={styles.carbonRowMain}>
                        <span className={styles.carbonLabel}>
                          {t('earnings.carbon.accruing')}
                          <span className={styles.carbonEstimateBadge}>
                            {t('earnings.carbon.estimateBadge')}
                          </span>
                        </span>
                        <span className={styles.carbonHint}>
                          {t('earnings.carbon.accruingHint', {
                            kwh: b.accruing_energy_kwh.toFixed(1),
                            year: carbonQ.data.program_year,
                            nextYear: carbonQ.data.program_year + 1,
                          })}
                        </span>
                      </div>
                      <span className={styles.carbonValueEstimate}>
                        {formatCents(b.accruing_estimate_cents, b.currency)}
                      </span>
                    </div>
                  )}
                  {b.realized_paid_cents > 0 && (
                    <div className={styles.carbonRow}>
                      <div className={styles.carbonRowMain}>
                        <span className={styles.carbonLabel}>{t('earnings.carbon.realized')}</span>
                        <span className={styles.carbonHint}>{t('earnings.carbon.realizedHint')}</span>
                      </div>
                      <span className={styles.carbonValueConfirmed}>
                        {formatCents(b.realized_paid_cents, b.currency)}
                      </span>
                    </div>
                  )}
                  {b.realized_unpaid_cents > 0 && (
                    <div className={`${styles.carbonRow} ${styles.carbonRowUnpaid}`}>
                      <div className={styles.carbonRowMain}>
                        <span className={styles.carbonLabel}>{t('earnings.carbon.unpaid')}</span>
                        <span className={styles.carbonHint}>
                          {carbonQ.data.needs_payout_setup
                            ? t('earnings.carbon.unpaidHintSetup')
                            : t('earnings.carbon.unpaidHintProcessing')}
                        </span>
                        {carbonQ.data.needs_payout_setup && (
                          <button
                            type="button"
                            className={styles.carbonCta}
                            disabled={carbonSetupBusy}
                            onClick={async () => {
                              setCarbonSetupBusy(true);
                              try {
                                const next = await startPayoutsOrChoose(connectQ.data);
                                if (next.kind === 'chooser') { onNavigate(chooserPath); return; }
                                window.location.href = next.url;
                              } catch {
                                toast(t('earnings.carbon.setupError'), { tone: 'error' });
                                setCarbonSetupBusy(false);
                              }
                            }}
                          >
                            {carbonSetupBusy
                              ? t('earnings.carbon.setupCtaBusy')
                              : t('earnings.carbon.setupCta')}
                          </button>
                        )}
                      </div>
                      <span className={styles.carbonValueConfirmed}>
                        {formatCents(b.realized_unpaid_cents, b.currency)}
                      </span>
                    </div>
                  )}
                </div>
              ))}
              {carbonQ.data.historical && (
                <p className={styles.carbonHistorical}>
                  {t('earnings.carbon.historical', {
                    kwh: carbonQ.data.historical.energy_kwh.toFixed(1),
                  })}
                </p>
              )}
            </div>
          )}

          {/* Stripe Express link — state-aware. While connect-status
              is in flight, the button renders disabled with a
              "Checking…" label so the resident doesn't see a flash
              between the two end states. After status resolves, the
              button branches: charges-enabled → open the Stripe
              Express dashboard in a new tab; otherwise → kick off
              onboarding. */}
          <div className={styles.stripeRow}>
            <p className={styles.stripeText}>
              {connectQ.isLoading
                ? t('earnings.stripe.checking')
                : connectQ.data?.charges_enabled
                  ? t('earnings.stripe.ready')
                  : buildingNoPayouts
                    ? t('earnings.stripe.building')
                    : t('earnings.stripe.setup')}
            </p>
            {!buildingNoPayouts && (
            <button
              type="button"
              className={styles.stripeBtn}
              disabled={connectQ.isLoading || openingDashboard || startingOnboarding}
              onClick={() => {
                // Branch B — not Connect-ready. Redirect to Stripe-
                // hosted onboarding. Full-page nav, no new tab.
                if (!connectQ.data?.charges_enabled) {
                  setStartingOnboarding(true);
                  void startPayoutsOrChoose(connectQ.data)
                    .then((next) => {
                      if (next.kind === 'chooser') { onNavigate(chooserPath); return; }
                      window.location.href = next.url;
                    })
                    .catch((err: unknown) => {
                      setStartingOnboarding(false);
                      toast(
                        err instanceof Error && err.message
                          ? err.message
                          : t('earnings.stripe.startFailed'),
                        { tone: 'error' },
                      );
                    });
                  return;
                }

                // Branch A — Connect-ready. iOS Safari pre-open
                // pattern: synchronously open about:blank in the
                // click-handler tick so the browser counts the new
                // window as user-initiated. Once the dashboard-link
                // fetch resolves, redirect the same window. If the
                // fetch fails, close the placeholder window so it
                // doesn't sit blank.
                //
                // NOTE: must NOT pass noopener/noreferrer here —
                // those flags force window.open to return null per
                // the HTML spec, breaking the pre-open pattern.
                // Stripe-hosted destination is trusted; we null
                // popup.opener after the redirect to close the
                // back-channel.
                const popup = window.open('about:blank', '_blank');
                setOpeningDashboard(true);
                void fetchDashboardLink()
                  .then(({ url }) => {
                    if (popup) {
                      popup.location.href = url;
                      popup.opener = null;
                    }
                  })
                  .catch(async (err: unknown) => {
                    if (popup) popup.close();
                    // 412 EXPRESS_NOT_READY — connect-status was
                    // stale (e.g., the resident's Express account
                    // was suspended after the page loaded). Fall
                    // through to onboarding.
                    const code = await readErrorCode(err);
                    if (code === 'EXPRESS_NOT_READY') {
                      try {
                        const next = await startPayoutsOrChoose(connectQ.data);
                        if (next.kind === 'chooser') { onNavigate(chooserPath); return; }
                        window.location.href = next.url;
                        return;
                      } catch {
                        // Falls through to the generic toast below.
                      }
                    }
                    toast(
                      t('earnings.stripe.openFailed'),
                      { tone: 'error' },
                    );
                  })
                  .finally(() => setOpeningDashboard(false));
              }}
            >
              {connectQ.isLoading ? (
                <><Loader2 size={13} className={styles.spin} /> {t('earnings.stripe.btnChecking')}</>
              ) : openingDashboard ? (
                <><Loader2 size={13} className={styles.spin} /> {t('earnings.stripe.btnOpening')}</>
              ) : startingOnboarding ? (
                <><Loader2 size={13} className={styles.spin} /> {t('earnings.stripe.btnOpening')}</>
              ) : connectQ.data?.charges_enabled ? (
                <><ExternalLink size={13} /> {t('earnings.stripe.btnDashboard')}</>
              ) : (
                <><ExternalLink size={13} /> {t('earnings.stripe.btnSetup')}</>
              )}
            </button>
            )}
          </div>

          {isEmpty && (
            <div className={styles.emptyHint}>
              {copyKind === 'building' ? (
                t('earnings.empty.building')
              ) : (
                <>
                  {t('earnings.empty.part1')}{' '}
                  <strong>{t('earnings.empty.strong')}</strong>{' '}
                  {copyKind === 'home' ? t('earnings.empty.part2Home') : t('earnings.empty.part2')}
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/** One currency's earnings figures. */
type EarningsLine = NonNullable<MyChargerEarnings['by_currency']>[number];

/** Every currency's figures, or the one set an older Api sends (USD). */
function earningsLines(data: MyChargerEarnings): EarningsLine[] {
  if (data.by_currency && data.by_currency.length > 0) return data.by_currency;
  return [{
    currency: data.currency ?? 'USD',
    all_time_cents: data.all_time_cents,
    this_month_cents: data.this_month_cents,
    last_30_days_cents: data.last_30_days_cents,
    last_payout: data.last_payout,
  }];
}

interface MoneyItem { cents: number; currency: string }

function SummaryCard({
  label, value, hint,
}: { label: string; value: ReactNode; hint?: string }) {
  return (
    <div className={styles.summaryCard}>
      <span className={styles.summaryLabel}>{label}</span>
      <span className={styles.summaryValue}>{value}</span>
      {hint && <span className={styles.summaryHint}>{hint}</span>}
    </div>
  );
}

function formatDate(d: Date): string {
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/** Pull a structured error code out of a ky HTTPError. Mirrors the
 *  helper in MyChargerDetailPage.tsx (not exported there — kept
 *  inline on each consumer until a third caller appears and the
 *  lift is worth it). Returns null on network errors or malformed
 *  responses; the caller falls through to the generic toast. */
async function readErrorCode(err: unknown): Promise<string | null> {
  const response = (err as { response?: Response }).response;
  if (!response) return null;
  try {
    const body = await response.clone().json() as { error?: { code?: string } };
    return body.error?.code ?? null;
  } catch {
    return null;
  }
}
